import { t, type I18nLocale } from '@ardoq/i18n';
import {
  EmptyState,
  EmptyStateToAddIllustration,
  InfoNotification,
} from '@ardoq/status-ui';
import { Text } from '@ardoq/typography';
import {
  DeleteConfirmationModal,
  DocumentArchiveViewProps,
  DocumentArchiveTableView,
  DropZone,
  MoveToModal,
} from '../../../documentArchive/DocumentArchivePageView';
import {
  ContentWrapperStack,
  PageTitle,
  PageTitleWrapper,
} from '../SharedComponents';
import { LinkButton } from '@ardoq/button';
import { FlexBox } from '@ardoq/layout';
import { UnstyledLinkProps, Link } from '@ardoq/link';

export type WorkspaceDocumentArchivePageViewProps = DocumentArchiveViewProps & {
  title: string;
  toOrganizationArchive: UnstyledLinkProps;
  helpArticleLink: UnstyledLinkProps;
  showEmptyState: boolean;
  i18nLocale: I18nLocale;
};

export const WorkspaceDocumentArchivePageView = (
  props: WorkspaceDocumentArchivePageViewProps
) => (
  <>
    <ContentWrapperStack height="full" width="full">
      <PageTitleWrapper>
        <PageTitle title={props.title} />
      </PageTitleWrapper>
      {props.showEmptyState ? (
        <TableEmptyState {...props} />
      ) : (
        <PopulatedTable {...props} />
      )}
    </ContentWrapperStack>
    <DeleteConfirmationModal {...props.deleteConfirmationModal} />
    <MoveToModal {...props.moveToModal} />
  </>
);

const PopulatedTable = (props: WorkspaceDocumentArchivePageViewProps) => (
  <>
    <FlexBox flexDirection="column" gap="small">
      <InfoNotification>
        <Text variant="text2">
          {t(
            props.i18nLocale,
            'workspaceManager:shareAssetsToOrgDocuments',
            'Share your assets across all workspaces by moving them to the organization documents. Select files to begin.'
          )}
        </Text>
      </InfoNotification>
      <FlexBox justify="end">
        <LinkButton {...props.toOrganizationArchive}>
          {t(
            props.i18nLocale,
            'workspaceManager:openOrganizationDocuments',
            'Open organization documents'
          )}
        </LinkButton>
      </FlexBox>
    </FlexBox>
    <FlexBox width="full" id={MOVE_TO_GLOBAL_DROP_ZONE_SELECTOR}>
      <DropZone {...props.dropZone} />
    </FlexBox>
    <DocumentArchiveTableView {...props.table} />
  </>
);

const TableEmptyState = (props: WorkspaceDocumentArchivePageViewProps) => (
  <FlexBox flexDirection="column" height="full">
    <FlexBox width="full" id={MOVE_TO_GLOBAL_DROP_ZONE_SELECTOR}>
      <DropZone {...props.dropZone} />
    </FlexBox>
    <FlexBox justify="center" align="center" height="full">
      <EmptyState
        title={t(
          props.i18nLocale,
          'workspaceManager:centralizeWorkspaceAssets',
          'Centralize your workspace assets'
        )}
        description={
          <Text variant="text2">
            {t(
              props.i18nLocale,
              'workspaceManager:useOrgDocumentsForSharedAssets',
              'Use this area for files specific to this workspace. For shared assets like logos and icons, use the organization documents to ensure they remain consistent across the platform and prevent broken images.'
            )}
          </Text>
        }
        layout="vertical"
        media={<EmptyStateToAddIllustration />}
      >
        <Link {...props.helpArticleLink}>
          {t(
            props.i18nLocale,
            'workspaceManager:learnMoreAboutArchives',
            'Learn more about archives'
          )}
        </Link>
        <LinkButton {...props.toOrganizationArchive}>
          {t(
            props.i18nLocale,
            'workspaceManager:openOrganizationDocuments',
            'Open organization documents'
          )}
        </LinkButton>
      </EmptyState>
    </FlexBox>
  </FlexBox>
);

export const MOVE_TO_GLOBAL_DROP_ZONE_SELECTOR = 'move-to-global-drop-zone';
